import { useContext, useEffect, useState } from 'react';
import { FieldValuesType, Steps, DecisionResultType, LoanType } from '../types';
import { AppContext } from '@/contexts/app-context';

export const SESSION_STORAGE_KEY = 'brandedConsumerLoanValues';
export const SESSION_STORAGE_STEP_KEY = 'progressStep';
export const SESSION_STORAGE_DECISION_KEY = 'brandedConsumerLoanDecision';

export const useValues = (loanType: LoanType = 'MOTOR') => {
    const context = useContext(AppContext);

    const getInitialValues = (): FieldValuesType => {
        return {
            governmentId: '',
            phoneMobile: '',
            contactEmail: '',
            purposeOfLoan: loanType,
            applicantMaritalStatus: '',
            numberChildrenNotOfAge: '',
            applicantHabitationForm: '',
            applicantEmploymentType: undefined,
            applicantEmployer: '',
            applicantEmploymentYearFrom: '',
            applicantEmploymentMonthFrom: '',
            applicantEmploymentYearUntil: '',
            applicantEmploymentMonthUntil: '',
            employmentTypeQualifying: '',
            montlyIncomeGross: '',
            livingExpenses: '',
            otherLoanExpenses: '',
            insurance: false,
            payoutAccount: {
                clearingNumber: '',
                accountNumber: '',
            },
            agreementSignType: 'E_SIGN',
            approveConditions: false,
        };
    };

    const [step, setStep] = useState<Steps>(0);
    const [values, setValues] = useState(getInitialValues);
    const [decisionResult, setDecisionResult] = useState<DecisionResultType | null>(null);

    const getValuesFromStorage = () => {
        const session = sessionStorage?.getItem(SESSION_STORAGE_KEY);
        if (!session || typeof session !== 'string') {
            return null;
        }

        return JSON.parse(session) ?? null;
    };

    useEffect(() => {
        const parsedValues = getValuesFromStorage();
        if (parsedValues) {
            setValues(parsedValues);
        }

        const savedStep = sessionStorage.getItem(SESSION_STORAGE_STEP_KEY);
        if (savedStep && context?.user?.isAuthenticated) {
            setStep(Number(savedStep) as Steps);
        }
    }, []);

    useEffect(() => {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(values));
    }, [values]);

    useEffect(() => {
        sessionStorage.setItem(SESSION_STORAGE_STEP_KEY, String(step));
    }, [step]);

    useEffect(() => {
        const storedDecision = sessionStorage.getItem(SESSION_STORAGE_DECISION_KEY);
        if (storedDecision) {
            setDecisionResult(JSON.parse(storedDecision));
        }
    }, []);

    useEffect(() => {
        if (decisionResult?.decision === 'APPROVED') {
            sessionStorage.setItem(SESSION_STORAGE_DECISION_KEY, JSON.stringify(decisionResult));
        } else {
            sessionStorage.removeItem(SESSION_STORAGE_DECISION_KEY);
        }
    }, [decisionResult]);

    const resetAll = () => {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_STEP_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_DECISION_KEY);
        setValues(getInitialValues());
        setStep(0);
        setDecisionResult(null);
    };

    return {
        values,
        setValues,
        step,
        setStep,
        getValuesFromStorage,
        resetAll,
        setDecisionResult,
        decisionResult,
    };
};
